<script setup lang="ts">
  import { ExternalRoute } from "@/constants/externalRoute"
  import { useEvChargerQuote as defaultUseEvChargerQuote } from "@/lib/useEvChargerQuote"
  import { useStepWizard as defaultUseStepWizard } from "@/lib/useStepWizard"
  import { AddressFinderModel, AddressNotListed } from "@/types/AddressType"
  import { templateRef } from "@vueuse/core"
  import { computed, inject, ref } from "vue"
  import { AddressFinder } from "@/components/ui/address-finder"
  import { Alert, AlertDescription } from "@/components/ui/alert"
  import Button from "@/components/ui/button/Button.vue"
  import { Link } from "@/components/ui/link"
  import { StepWizardProgressCard } from "@/components/ui/step-wizard"

  const useEvChargerQuote = inject(
    "useEvChargerQuote",
    defaultUseEvChargerQuote,
  )
  const useStepWizard = inject("useStepWizard", defaultUseStepWizard)

  const { updateAddress } = useEvChargerQuote()
  const { goNext } = useStepWizard()

  const addressFinderModel = ref<AddressFinderModel>()

  const isAddressNotListed = computed(
    () => addressFinderModel.value === AddressNotListed,
  )

  const addressFinder = templateRef("addressFinder")
  const onSubmit = async () => {
    await addressFinder.value?.onSubmitPostcode()

    if (
      addressFinderModel.value &&
      addressFinderModel.value !== AddressNotListed
    ) {
      updateAddress(addressFinderModel.value)
      goNext()
    }
  }
</script>

<template>
  <form novalidate @submit.prevent="onSubmit">
    <StepWizardProgressCard title="Your address">
      <p class="mb-4 sm:mb-6">
        Please enter your postcode to receive an EV charger quote.
      </p>
      <AddressFinder
        ref="addressFinder"
        v-model="addressFinderModel"
      ></AddressFinder>
      <Alert v-if="isAddressNotListed" variant="warning" class="mt-4 sm:mt-8">
        <AlertDescription>
          <p>
            We're having trouble finding your address. Please email
            <Link class="link text-primary-600" to="mailto:ev@so.energy"
              >ev@so.energy</Link
            >
            with a
            <Link class="link text-primary-600" :to="ExternalRoute.what3Words"
              >what3words</Link
            >
            link to your location, and we'll be in touch.
          </p>
        </AlertDescription>
      </Alert>
      <template #next_button>
        <Button
          variant="tonal"
          type="submit"
          size="md"
          :disabled="isAddressNotListed"
        >
          Next
        </Button>
      </template>
    </StepWizardProgressCard>
  </form>
</template>
